import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle, GraduationCap, Shield, ChevronRight, ChevronLeft,
  Check, Sparkles, Loader2, FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { TUTORIALS } from "@/data/tutorials";
import api from "@/lib/api";

/**
 * TutorialDrawer — one small "?" button in the header opens this right-side
 * sheet. Users pick a tour, then walk step-by-step. On steps that reference a
 * page, we navigate there and briefly ring the target nav item so it feels
 * "interactive" without needing a heavyweight tour library.
 *
 * All AI-assist and approval buttons reuse existing backend endpoints —
 * no new server code is required.
 */

const HighlightRing = ({ testId }) => {
  useEffect(() => {
    if (!testId) return undefined;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) return undefined;
    el.classList.add("tut-ring");
    return () => el.classList.remove("tut-ring");
  }, [testId]);
  return null;
};

const StepBody = ({ tour, step, tourId, entities }) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiInput, setAiInput] = useState("");
  const [approvalCreating, setApprovalCreating] = useState(false);
  const [approvalDoneId, setApprovalDoneId] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState("");

  const askVaelithon = async () => {
    if (!tour.aiAssist || !aiInput.trim()) return;
    if (!selectedBiz) {
      toast.error("Pick which business this applies to first.");
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/v1/regalis/router", {
        request_type: tour.aiAssist.request_type,
        business_id: selectedBiz,
        industry_profile: tour.aiAssist.industry_profile,
        message: tour.aiAssist.messageTemplate.replace("{{userInput}}", aiInput.trim()),
        context: { source: "tutorial", tour: tourId, step: step.title },
      });
      setAiResult(data);
      if (data.approval_id) {
        toast.success("Vaelithon Reign opened an approval for you.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vaelithon Reign request failed.");
    } finally {
      setAiLoading(false);
    }
  };

  const createApproval = async () => {
    if (!step.createApproval) return;
    if (!selectedBiz) {
      toast.error("Pick which business this applies to first.");
      return;
    }
    setApprovalCreating(true);
    try {
      const { data } = await api.post("/approvals", {
        entity_id: selectedBiz,
        type: step.createApproval.type,
        title: step.createApproval.title,
        description: step.createApproval.description,
        priority: "medium",
        metadata: { source: "tutorial", tour: tourId },
      });
      setApprovalDoneId(data.id);
      toast.success("Approval added to the desk.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create approval.");
    } finally {
      setApprovalCreating(false);
    }
  };

  const needsBiz = !!(step.createApproval || tour.aiAssist);

  return (
    <div className="space-y-4">
      <p className="text-sm text-textmuted leading-relaxed" data-testid="tut-step-why">
        {step.why}
      </p>

      {step.do && step.do.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2 text-[10px]">Do this</div>
          <ul className="space-y-1.5">
            {step.do.map((d, i) => (
              <li key={i} className="flex gap-2 text-sm text-textmain">
                <ChevronRight className="w-4 h-4 mt-0.5 text-champagne shrink-0" strokeWidth={1.6} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step.docs && step.docs.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2 text-[10px] flex items-center gap-1.5">
            <FileText className="w-3 h-3" strokeWidth={1.6} /> Paperwork to keep
          </div>
          <ul className="space-y-1 rounded-md border border-seam bg-elevated/40 p-3">
            {step.docs.map((d, i) => (
              <li key={i} className="text-xs text-textmuted flex gap-2">
                <span className="text-champagne">•</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {needsBiz && (
        <div>
          <label className="label-eyebrow text-[10px] block mb-1">
            Which business is this for?
          </label>
          <select
            data-testid="tut-biz-select"
            value={selectedBiz}
            onChange={(e) => setSelectedBiz(e.target.value)}
            className="w-full rounded-md border border-seam bg-obsidian text-sm text-textmain px-2 py-1.5"
          >
            <option value="">Select…</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {step.createApproval && (
        <Button
          type="button"
          onClick={createApproval}
          disabled={approvalCreating || !!approvalDoneId}
          data-testid="tut-create-approval-btn"
          size="sm"
          className="bg-champagne text-obsidian hover:bg-champagne/90"
        >
          {approvalCreating ? (
            <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Creating…</>
          ) : approvalDoneId ? (
            <><Check className="w-3.5 h-3.5 mr-2" /> Sent to Approvals</>
          ) : (
            <>Turn this step into an approval item</>
          )}
        </Button>
      )}

      {tour.aiAssist && (
        <div className="rounded-md border border-champagne/30 bg-champagne/5 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] text-champagne">
            <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Customize with Vaelithon Reign
          </div>
          <textarea
            data-testid="tut-ai-input"
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            rows={2}
            placeholder="e.g. 12 acres in Ohio, no structures, RSVP LLC is sole owner, buyer already found"
            className="w-full text-xs rounded bg-obsidian border border-seam text-textmain p-2 placeholder-textmuted"
          />
          <Button
            type="button"
            size="sm"
            onClick={askVaelithon}
            disabled={aiLoading || !aiInput.trim()}
            data-testid="tut-ai-run"
            className="bg-elevated border border-champagne/40 text-champagne hover:bg-champagne/10"
          >
            {aiLoading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Thinking…</>
            ) : (
              <>Get a plan tailored to me</>
            )}
          </Button>
          {aiResult && (
            <div data-testid="tut-ai-result" className="text-[11px] text-textmuted whitespace-pre-wrap max-h-64 overflow-auto border-t border-seam pt-2 mt-1">
              <div className="text-textmain font-medium mb-1">
                {aiResult.data?.summary || "Plan ready."}
              </div>
              {aiResult.data?.recommended_actions?.map((a, i) => (
                <div key={i} className="mb-1">
                  • <span className="text-textmain">{a.action}</span>
                  {a.owner ? <span className="text-champagne"> — {a.owner}</span> : null}
                  {a.eta ? <span className="text-textmuted"> ({a.eta})</span> : null}
                </div>
              ))}
              {aiResult.requires_human_approval && aiResult.approval_id && (
                <div className="mt-2 text-champagne">
                  Approval item created: {aiResult.approval_id.slice(0, 8)}…
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TutorialDrawer = () => {
  const [open, setOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [entities, setEntities] = useState([]);
  const { admin } = useAuth();
  const navigate = useNavigate();

  const isOwner = admin?.role === "owner_ceo";
  const availableTours = TUTORIALS.filter((t) => !t.ownerOnly || isOwner);

  useEffect(() => {
    if (!open || entities.length > 0) return;
    api.get("/enterprise/entities")
      .then((r) => setEntities(r.data || []))
      .catch(() => setEntities([]));
  }, [open, entities.length]);

  const tour = activeTour ? TUTORIALS.find((t) => t.id === activeTour) : null;
  const step = tour ? tour.steps[stepIdx] : null;

  const gotoStep = (idx) => {
    setStepIdx(idx);
    const s = tour?.steps[idx];
    if (s?.nav) navigate(s.nav);
  };

  const closeTour = () => {
    setActiveTour(null);
    setStepIdx(0);
  };

  if (!admin) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="tutorial-open-btn"
        title="Tutorials"
        className="p-2 rounded-md border border-seam text-textmuted hover:text-champagne hover:border-champagne/40 transition-colors"
      >
        <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeTour(); }}>
        <SheetContent
          side="right"
          data-testid="tutorial-drawer"
          className="w-full sm:max-w-md bg-obsidian border-l border-seam text-textmain overflow-y-auto"
        >
          {!tour ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-textmain font-heading text-2xl flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-champagne" strokeWidth={1.5} />
                  Tutorials
                </SheetTitle>
                <SheetDescription className="text-textmuted">
                  Simple walkthroughs, one click at a time.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-3">
                {availableTours.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`open-${t.testId}`}
                    onClick={() => { setActiveTour(t.id); setStepIdx(0); if (t.steps[0]?.nav) navigate(t.steps[0].nav); }}
                    className="w-full text-left rounded-lg border border-seam bg-elevated/40 p-4 hover:border-champagne/50 hover:bg-elevated/60 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {t.ownerOnly ? (
                        <Shield className="w-4 h-4 text-champagne mt-0.5" strokeWidth={1.6} />
                      ) : (
                        <GraduationCap className="w-4 h-4 text-champagne mt-0.5" strokeWidth={1.6} />
                      )}
                      <div className="flex-1">
                        <div className="text-textmain font-medium text-sm flex items-center gap-2">
                          {t.title}
                          {t.ownerOnly && (
                            <span className="text-[9px] tracking-widest uppercase text-champagne border border-champagne/40 rounded px-1.5 py-0.5">
                              CEO only
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-textmuted mt-1">{t.subtitle}</div>
                        <div className="text-[10px] text-textmuted mt-2">
                          {t.steps.length} steps
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-champagne">
                  {tour.ownerOnly && <Shield className="w-3 h-3" strokeWidth={1.6} />}
                  {tour.ownerOnly ? "Private playbook" : "Guided tour"}
                </div>
                <SheetTitle className="text-textmain font-heading text-xl">
                  {step.title}
                </SheetTitle>
                <SheetDescription className="text-textmuted">
                  Step {stepIdx + 1} of {tour.steps.length} — {tour.title}
                </SheetDescription>
              </SheetHeader>

              {step.highlightTestId && <HighlightRing testId={step.highlightTestId} />}

              <div className="mt-4 pb-24">
                <StepBody tour={tour} step={step} tourId={tour.id} entities={entities} />
              </div>

              <div className="fixed bottom-0 right-0 w-full sm:max-w-md p-4 bg-obsidian border-t border-seam flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={closeTour}
                    data-testid="tut-exit-btn"
                    className="text-textmuted hover:text-textmain"
                  >
                    Exit
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => gotoStep(Math.max(0, stepIdx - 1))}
                    disabled={stepIdx === 0}
                    data-testid="tut-prev-btn"
                    className="border-seam text-textmain"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
                  </Button>
                  {stepIdx < tour.steps.length - 1 ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => gotoStep(stepIdx + 1)}
                      data-testid="tut-next-btn"
                      className="bg-champagne text-obsidian hover:bg-champagne/90"
                    >
                      Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      onClick={closeTour}
                      data-testid="tut-finish-btn"
                      className="bg-champagne text-obsidian hover:bg-champagne/90"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Done
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default TutorialDrawer;
